// src/index.tsx
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { WizardProvider } from "./context/WizardContext";
import { AuthProvider } from "./context/AuthContext";
import { AppConfigProvider } from "./context/AppConfigContext";
import { UiLocaleProvider } from "./context/UiLocaleContext";
import { preloadStartupAssets } from "./bootstrapPreload";
import LogoLoader from "./components/common/LogoLoader";

// Attach bearer token to all fetch calls (Bearer auth preference).
// This avoids updating every component that calls the protected API.
const TOKEN_STORAGE_KEY = "mysipur_auth_token";
if (typeof window !== "undefined" && typeof window.fetch === "function") {
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) return originalFetch(input, init);

    const headers = new Headers(init.headers || {});
    if (!headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    return originalFetch(input, { ...init, headers });
  };
}

const StartupLoader = () => (
  <div className="relative flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 px-4">
    <main
      id="main-content"
      tabIndex={-1}
      className="flex flex-col items-center outline-none"
      aria-busy="true"
      aria-live="polite"
    >
      <LogoLoader size="lg" />
      <p className="mt-4 max-w-md text-center text-base font-semibold text-brandNavy">
        Loading your experience...
      </p>
    </main>
  </div>
);

const BootstrapApp = () => {
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    preloadStartupAssets()
      .catch(() => {
        // Keep UI usable even if preload fails for some assets.
      })
      .finally(() => {
        if (mounted) setReady(true);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (!ready) return <StartupLoader />;

  return (
    <AppConfigProvider>
      <UiLocaleProvider>
        <AuthProvider>
          <WizardProvider>
            <App />
          </WizardProvider>
        </AuthProvider>
      </UiLocaleProvider>
    </AppConfigProvider>
  );
};

const rootElement = document.getElementById('root');

if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <BootstrapApp />
    </React.StrictMode>
  );
}
